import { Header } from '@components/Header';
import { routes } from '@routes';
import { Helmet } from 'react-helmet-async';
import { AuthCallout } from '../components/AuthCallout';
import { EndpointCard } from '../components/EndpointCard';
import { EndpointSection } from '../components/EndpointSection';
import { MethodBadgeGet } from '../components/MethodBadgeGet';
import { MethodBadgePost } from '../components/MethodBadgePost';
import { ParamRow } from '../components/ParamRow';
import { ParamsTable } from '../components/ParamsTable';

export const OrganizationApiDocsPage = () => (
  <>
    <Helmet>
      <title>Organization API Docs - AutoScript Admin</title>
    </Helmet>
    <Header title="Organization API" backRoute={routes.adminApiDocs.make()} />
    <div className="mx-auto max-w-4xl px-6 py-6">
      <AuthCallout>
        Every request must send an Organization API Access Key in the <code className="font-mono text-xs">api-key</code>{' '}
        header. Keys are issued per organization and may carry an expiration date. The key must belong to the
        organization referenced by the <code className="font-mono text-xs">oid</code> path parameter, and any workspace
        or job referenced in the path must belong to that same organization, otherwise the request is rejected with{' '}
        <code className="font-mono text-xs">403 Forbidden</code>.
      </AuthCallout>

      <EndpointCard method={<MethodBadgePost />} path="/api/public/v1/{oid}/{wsid}/job" summary="Create a new job">
        <EndpointSection title="Path parameters">
          <ParamsTable>
            <ParamRow name="oid" type="string" required description="Organization ID the API key belongs to." />
            <ParamRow name="wsid" type="string" required description="Workspace ID the job is created in." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Request body">
          <ParamsTable>
            <ParamRow name="notes" type="string" required description="Free-text notes for the job." />
            <ParamRow name="attachments" type="string[]" required description="File names attached to the job." />
            <ParamRow name="name" type="string" description="Display name of the job." />
            <ParamRow name="status" type="string" description="Initial job status." />
            <ParamRow name="deadline" type="string (ISO date)" description="Due date for the job." />
            <ParamRow name="tags" type="string[]" description="Tags shown on the job." />
            <ParamRow
              name="multichannel"
              type="boolean"
              description="Whether the audio should be treated as multichannel."
            />
            <ParamRow
              name="…"
              description="Accepts the remaining optional job fields from the job DTO, such as meeting metadata, assignee fields, repositoryId, and layoutId."
            />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="201" type="empty" description="Job created." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard
        method={<MethodBadgeGet />}
        path="/api/public/v1/{oid}/workspaces"
        summary="List workspaces in the organization"
      >
        <EndpointSection title="Path parameters">
          <ParamsTable>
            <ParamRow name="oid" type="string" required description="Organization ID the API key belongs to." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="200" type="JSON array" description="Workspaces belonging to the organization." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard method={<MethodBadgeGet />} path="/api/public/v1/{oid}/{jobId}" summary="Get job details">
        <EndpointSection title="Path parameters">
          <ParamsTable>
            <ParamRow name="oid" type="string" required description="Organization ID the API key belongs to." />
            <ParamRow name="jobId" type="string" required description="Job ID; must belong to the organization." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="200" type="JSON object" description="The job with its current state." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard
        method={<MethodBadgePost />}
        path="/api/public/v1/{oid}/{jobId}/resubmit"
        summary="Resubmit a job through the pipeline"
      >
        <EndpointSection title="Description">
          <p>Equivalent to the admin Resubmit button. Re-uses the existing media and does not re-upload.</p>
        </EndpointSection>
        <EndpointSection title="Path parameters">
          <ParamsTable>
            <ParamRow name="oid" type="string" required description="Organization ID the API key belongs to." />
            <ParamRow name="jobId" type="string" required description="Job ID; must belong to the organization." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="201" type="JSON object" description="Resubmission result." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard
        method={<MethodBadgeGet />}
        path="/api/public/v1/{oid}/{jobId}/upload-params"
        summary="Get S3 upload params for job files"
      >
        <EndpointSection title="Path parameters">
          <ParamsTable>
            <ParamRow name="oid" type="string" required description="Organization ID the API key belongs to." />
            <ParamRow name="jobId" type="string" required description="Job ID; must belong to the organization." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Query parameters">
          <ParamsTable>
            <ParamRow name="file" type="string" required description="Name of the file that will be uploaded." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow
              name="200"
              type="JSON object"
              description="Presigned S3 POST target: url plus the form fields to include in the upload."
            />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard
        method={<MethodBadgePost />}
        path="/api/public/v1/{oid}/{jobId}/file"
        summary="Attach an S3-uploaded file to a job"
      >
        <EndpointSection title="Path parameters">
          <ParamsTable>
            <ParamRow name="oid" type="string" required description="Organization ID the API key belongs to." />
            <ParamRow name="jobId" type="string" required description="Job ID; must belong to the organization." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Request body">
          <ParamsTable>
            <ParamRow name="fileName" type="string" required description="Name of the uploaded file." />
            <ParamRow name="fileSize" type="string" required description="Human-readable file size." />
            <ParamRow name="weight" type="number" required description="File size in bytes." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="201" type="string" description="ID of the attached media record." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard method={<MethodBadgePost />} path="/api/public/v1/{oid}/{jobId}/submit" summary="Submit a job">
        <EndpointSection title="Description">
          <p>Submits the job into the processing pipeline once its files are attached.</p>
        </EndpointSection>
        <EndpointSection title="Path parameters">
          <ParamsTable>
            <ParamRow name="oid" type="string" required description="Organization ID the API key belongs to." />
            <ParamRow name="jobId" type="string" required description="Job ID; must belong to the organization." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="201" type="boolean" description="Whether the job was submitted." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>
    </div>
  </>
);
